"use client";

import classNames from "classnames";
import {
  HTMLProps,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import Icon from "./icon";

type TeaserSliderProps = {
  outerClassName?: string;
  buttonClassName?: string;
  classNamme?: HTMLProps<HTMLElement>["className"];
  buttonWrapperClassName?: HTMLProps<HTMLElement>["className"];
  darkMode?: boolean;
} & PropsWithChildren;

const TeaserSlider = (props: TeaserSliderProps) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [isSlider, setIsSlider] = useState(false);
  const [sliderPosition, setSliderPosition] = useState<
    "left" | "middle" | "right"
  >("left");

  const debounceFunction = (cb: () => void, delay: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => cb(), delay);
    };
  };

  const handleScroll = () => {
    const current = sliderRef?.current;
    if (!current) return;

    const offsetRight = Math.floor(
      current.scrollWidth - current.clientWidth - current.scrollLeft
    );
    const offsetLeft = current.scrollLeft;

    if (offsetRight <= 0) return setSliderPosition("right");
    if (offsetLeft <= 0) return setSliderPosition("left");
    setSliderPosition("middle");
  };

  useEffect(() => {
    const current = sliderRef?.current;
    const debounce = debounceFunction(handleScroll, 50);
    current?.addEventListener("scroll", debounce);

    return () => {
      current?.removeEventListener("scroll", debounce);
    };
  }, []);

  const handleResize = () => {
    const containerWidth = sliderRef?.current?.clientWidth || 0;
    const sliderWidth = sliderRef?.current?.children[0].scrollWidth || 0;
    if (sliderWidth > containerWidth) setIsSlider(true);
    if (sliderWidth <= containerWidth) setIsSlider(false);
  };

  useEffect(() => {
    handleResize();
    const debounce = debounceFunction(handleResize, 150);
    window.addEventListener("resize", debounce);

    return () => {
      window.removeEventListener("resize", debounce);
    };
  }, []);

  const handleNav = (direction: "left" | "right") => {
    sliderRef?.current?.scrollBy({
      left:
        direction == "right"
          ? sliderRef.current?.clientWidth
          : -sliderRef?.current?.clientWidth,
      behavior: "smooth",
    });
  };

  return (
    <div className="container relative !mt-0 md:mt-8 ">
      <div
        className={classNames("relative", props.outerClassName, {
          "after:opacity-0": sliderPosition === "right",
          "": isSlider,
        })}
      >
        <div
          ref={sliderRef}
          className={`relative scroll-snap-type-x snap-x snap-mandatory overflow-auto overflow-y-hidden hide-scrollbar ${
            props.classNamme ? props.classNamme : ""
          }`}
        >
          {props.children}
        </div>
      </div>
      {isSlider && (
        <div
          className={`hidden md:flex pointer-events-none [&>*]:pointer-events-auto space-x-1 justify-end items-end -translate-y-1/2s mt-2 h-[36px] relative right-0 ${props.buttonWrapperClassName}`}
        >
          <button
            aria-label="Föregående"
            disabled={sliderPosition === "left"}
            className={classNames(
              "rounded-sm h-10 w-10 items-center flex justify-center",
              props.darkMode
                ? "bg-static-black-400 hover:bg-static-black-500 disabled:opacity-30"
                : "bg-sand-600 hover:bg-sand-700 disabled:opacity-30",
              props.buttonClassName
            )}
            onClick={() => handleNav("left")}
          >
            <Icon.AngleLeft className="rotate-180" />
          </button>
          <button
            aria-label="Nästa"
            disabled={sliderPosition === "right"}
            className={classNames(
              "rounded-sm h-10 w-10 items-center flex justify-center",
              props.darkMode
                ? "bg-static-black-400 hover:bg-static-black-500 disabled:opacity-30"
                : "bg-sand-600 hover:bg-sand-700 disabled:opacity-30",
              props.buttonClassName
            )}
            onClick={() => handleNav("right")}
          >
            <Icon.AngleRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default TeaserSlider;
